import { Component, createRef } from 'react';

import { IOC_TYPES } from '../../../context/ioc.types';
import { lazyInject } from '../../../context/module';
import { FileUpload } from '../../../models/file-upload';
import { FormControl } from '../../../models/forms';
import { AuthService } from '../../../services/auth.service';
import { FileDownloadService } from '../../../services/file-download.service';
import { UIContext } from '../../../services/ui-context';
import { ProcessTypes } from '../../../types/types';
import { BrowserUtils } from '../../../ui-utils/browser-utils';
import { CCanvas } from '../../../utils/ccanvas';
import { ConvertOptionsUtils } from '../../../utils/convert-options-utils';
import { getBasename, getExtension } from '../../../utils/file';
import { findIndex } from '../../../utils/lodash-min';
import { ProcessorMetadataUtils } from '../../../utils/processor-metadata.utils';
import { ImageHelper } from '../../meme/meme-image';
import Modal from '../modal/modal.component';
import styles from './single-file-download.component.module.scss';

declare var pdfjsLib;

interface SingleFileDownloadModalComponentProps {
    title: string;
    files: any[];
    name: string;
    numberOfFiles: number;
    fileInfo: FileUpload;
    closeBtnName: string;
}

interface SingleFileDownloadModalComponentState {
    hasThumnails: boolean;
    preview: boolean
}

export class SingleFileDownloadModalComponent extends Component<SingleFileDownloadModalComponentProps> {
    list: any[] = [];

    static pdfThumDataURLMap = new Map<string, string>();
    readyPromise: Promise<any>;

    showPreview = new FormControl(false);
    processed = {};

    modalRef = createRef<Modal>();
    private generalFileIndex = 0;
    // @ViewChildren('thumbCanvases') thumbCanvases: QueryList<ElementRef>;

    @lazyInject(FileDownloadService) private downloadService: FileDownloadService;
    @lazyInject(AuthService) public auth: AuthService;
    @lazyInject(UIContext) private uiContext: UIContext;

    readonly state: Readonly<SingleFileDownloadModalComponentState> = {
        hasThumnails: false,
        preview: false,
    };
    constructor(
        props
    ) {
        super(props);
        this.readyPromise = Promise.all<{}>([
            new Promise<{}>((resolve, reject) => {
                if (!BrowserUtils.hasWindow() || window['pdfjsLib']) {
                    resolve({});
                } else {
                    const node = document.createElement('script');
                    node.src = '/js/pdf.min.js';
                    node.type = 'text/javascript';
                    node.async = true;
                    node.charset = 'utf-8';
                    (node as any).onload = resolve;
                    node.onerror = reject;
                    document.getElementsByTagName('head')[0].appendChild(node);
                }
            }),
            new Promise<{}>((resolve, reject) => {
                // TODO Fix this for prerender
                if (!BrowserUtils.hasWindow() || window['pdfjsWorker']) {
                    resolve({});
                } else {
                    const node = document.createElement('script');
                    node.src = '/js/pdf.worker.min.js';
                    node.type = 'text/javascript';
                    node.async = true;
                    node.charset = 'utf-8';
                    (node as any).onload = resolve;
                    node.onerror = reject;
                    document.getElementsByTagName('head')[0].appendChild(node);
                }
            }),
        ]);

        this.showPreview.valueChanges.subscribe(
            val => {
                this.setState({
                    preview: val
                });
                this.update().catch((err) => console.log(err));
            },
        );
    }

    componentDidMount() {
        if (this.isPDF()) {
            this.setState({
                hasThumnails: true
            });
        }
    }

    private isPDF() {
        return this.props.fileInfo.processType === 'split' && this.props.fileInfo.toType === 'pdf';
    }

    async update() {
        this.readyPromise.then(async () => {
            if (this.showPreview.value) {
                const canvases = Array.from(document.querySelectorAll<HTMLCanvasElement>('.thumbCanvases'))
                for (const el of canvases) {
                    const url = el.getAttribute('data-url');
                    const file_name = el.getAttribute('data-filename');
                    if (this.isPDF()) {
                        if (!this.getPDFThumDataURL(file_name)) {
                            await this.pdfjsThumb(el, file_name, url);
                        }
                    } else {
                        if (!this.getPDFThumDataURL(file_name)) {
                            const img = await ImageHelper.loadImage(url)
                            const displayCanvas = el.closest('div').querySelector<HTMLCanvasElement>('.display-canvas');
                            CCanvas.drawImage(el, img);
                            CCanvas.copyCanvas(el, displayCanvas);
                            // const u = await CCanvas.canvasToWebPBlob(el, 100)
                            // SingleFileDownloadModalComponent.pdfThumDataURLMap.set(file_name, displayCanvas.toDataURL('image/jpeg'));
                            CCanvas.drawImage(el, img);
                        }
                    }
                }
            }
        }).catch((err) => console.log(err));
    }

    onDownloadBtnHandler(file_name) {
        this.downloadService.downloadFile(this.props.fileInfo, file_name);
    }

    directDownloadHandler(file_name) {
        const link = this.downloadService.getDirectDownloadLink(this.props.fileInfo, file_name);
        this.uiContext.showSuccessDialog("Download Links", null, `
                    <div style="text-align: left;">
                        <div>
                            Filename: 
                            <div className="alert alert-info word-break-all">${file_name}</div>
                        </div>
                        <div>
                            Link:
                            <div className="alert alert-info word-break-all">${link}</div>
                        </div>
                        <div>*To download file directly, copy and paste above link to your browser's address bar.</div>
                    </div>
                `);
    }

    getDownloadURL(file_name) {
        return this.downloadService.getDownloadURL(this.props.fileInfo, file_name);
    }


    decodeFileName(name: string) {
        this.props.name;
        const fileNameTokens = (this.getAcualFileOutputFromOutput(name) || name).split(/-z-|-y-/);
        const fname = fileNameTokens[fileNameTokens.length - 1];

        if (this.props.fileInfo.fromType.includes('archive')) {
            return fname;
        }

        // `any`: reassigned below to both a numeric output index and a parsed
        // int, and typing ProcessResult made the inferred `string` bite.
        let index: any = fileNameTokens[fileNameTokens.length - 2];

        if (this.props.fileInfo.processType === ProcessTypes.SPLIT) {
            const splitType = ConvertOptionsUtils.getPropertyValue(this.props.fileInfo, 'split');
            const ranges = ConvertOptionsUtils.getSubPropertyValue(splitType, 'ranges')
            if (/page.by.page/i.test(splitType) || ranges != null) {
                index = this.getOutputIndex(name) + 1;
            } else {
                index = parseInt(index, 10) + 1;
            }

            const originalName = getBasename(this.props.name);
            const prefix = /page.by.page/i.test(splitType) ? 'Page' : 'Part';
            return `${originalName} - ${prefix} ${index}.pdf`;
        }

        return this.getIndexedFileName(name, index);
    }

    private getIndexedFileName(outputFilename: string, index: number) {
        const inputFileBasename = getBasename(this.props.name);
        const outputFileExtension = getExtension(outputFilename);
        return `${inputFileBasename} ${index}.${outputFileExtension}`;
    }

    private getAcualFileOutputFromOutput(output_fname) {
        try {
            const index = this.getOutputIndex(output_fname);
            return (this.props.fileInfo.result.originalOutput || this.props.fileInfo.result.output)[index];
        } catch (e) {
            console.error(e);
        }
    }

    private getOutputIndex(output_fname) {
        try {
            return findIndex(this.props.fileInfo.result.output, f => f === output_fname);
        } catch (e) {
            console.error(e);
        }
        return -1;
    }

    async pdfjsThumb(canvas: HTMLCanvasElement, file_name: string, pdfUrl: string) {
        // this.ngZone.runOutsideAngular(async () => {
        const pdfDocument = await pdfjsLib.getDocument(pdfUrl);
        //
        // Fetch the first page
        //
        const page = await pdfDocument.getPage(1);

        const viewport = page.getViewport(1);
        //
        // Prepare canvas using PDF page dimensions
        //
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        //
        // Render PDF page into canvas context
        //
        const task = page.render({ canvasContext: context, viewport: viewport });
        await task.promise.then(() => {
            // this.ngZone.run(() => {
            const displayCanvas = canvas.closest('div').querySelector<HTMLCanvasElement>('.display-canvas');
            CCanvas.copyCanvas(canvas, displayCanvas);
            SingleFileDownloadModalComponent.pdfThumDataURLMap.set(file_name, canvas.toDataURL('image/jpeg'));
            // });
        });
        // });
    }

    getPDFThumDataURL(file_name: string) {
        return SingleFileDownloadModalComponent.pdfThumDataURLMap.get(file_name);
    }

    getModalBody() {
        return <div data-modalbody>
            <div className={styles['file-list']} data-jkey="single-file-download-modal">
                {
                    this.props.files.map(file_name => {
                        return <div key={file_name} className={styles['file-item']}>
                            {
                                this.state.preview && <div className={styles['preview']}>
                                    {
                                        !this.getPDFThumDataURL(file_name) && <canvas className="display-canvas"></canvas>
                                    }
                                    <canvas className="thumbCanvases" style={{ display: 'none' }} data-filename={file_name}
                                        data-url={this.getDownloadURL(file_name)}></canvas>
                                    {
                                        this.getPDFThumDataURL(file_name) && <img src={this.getPDFThumDataURL(file_name)} alt="preview" />
                                    }
                                </div>
                            }
                            <div className={styles['file-info']}>
                                <div className={styles['file-name']} data-jkey="file-download-item">{this.decodeFileName(file_name)}</div>
                            </div>
                            <div className={styles['file-actions']}>
                                <button className={`${styles['btn']} ${styles['btn-primary']}`} data-url={this.getDownloadURL(file_name)}
                                    onClick={() => this.onDownloadBtnHandler(file_name)}>Download</button>
                                <button className={styles['btn']}
                                    data-url={this.getDownloadURL(file_name)} onClick={() => this.directDownloadHandler(file_name)}>
                                    Link
                                </button>
                            </div>
                        </div>
                    })
                }
            </div>
        </div>;
    }

    render() {
        return <Modal ref={this.modalRef} modalClass={styles['modal']}>
            <div data-modalheader className={styles['header']}>
                <h4>{this.props.title}</h4>
                <label className={styles['toggle']}>
                    <input type="checkbox" checked={this.state.preview} onChange={(e) => this.showPreview.setValue(e.target.checked)} />
                    Show Preview
                </label>
            </div>
            {
                this.getModalBody()
            }
        </Modal>
    }
}
